import { BoxGeometry, ConeGeometry, CylinderGeometry, Group, Mesh, type BufferGeometry, type Material, type MeshStandardMaterial } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { applyCharacterSurface, type CharacterSurfaceHandle } from "../render/character-surface/apply.ts";
import { actorStyle } from "../render/character-surface/config.ts";
import { characterBaseMaterial } from "../render/character-surface/material.ts";
import { AVATAR_RIM } from "../render/shaders/avatar.ts";
import { unindexTree } from "../render/geometry/unindexed.ts";

/** Partes articuladas do boneco (pivôs nos ombros, quadris e pescoço). */
export interface AvatarRig {
  root: Group;
  body: Group;
  head: Group;
  leftArm: Group;
  rightArm: Group;
  leftLeg: Group;
  rightLeg: Group;
  /** Ponto da mão direita onde o item empunhado é preso. */
  hand: Group;
  /** A superfície dos atores no boneco (F119): energia, dissolve e descarte. */
  surface: CharacterSurfaceHandle;
}

const SKIN = "#C68B59";
const PANTS = "#3A3F4A";
const STRAW = "#E2C27A";
const STRAW_BAND = "#8A3B2E";

// Geometrias compartilhadas por todos os bonecos (uma alocação só).
const geometries = {
  torso: new BoxGeometry(0.46, 0.6, 0.28),
  head: new BoxGeometry(0.3, 0.3, 0.3),
  limb: new BoxGeometry(0.14, 0.62, 0.16),
  leg: new BoxGeometry(0.17, 0.78, 0.18),
  brim: new CylinderGeometry(0.34, 0.34, 0.03, 10),
  crown: new ConeGeometry(0.17, 0.2, 8),
  band: new CylinderGeometry(0.175, 0.175, 0.04, 8),
};

function part(geometry: BufferGeometry, material: Material, x: number, y: number, z: number): Mesh {
  const mesh = new Mesh(geometry, material);
  mesh.position.set(x, y, z);
  return mesh;
}

/** Pivô na articulação: a peça pendurada abaixo dele (ou acima, para a cabeça). */
function joint(x: number, y: number, z: number, ...children: Mesh[]): Group {
  const g = new Group();
  g.position.set(x, y, z);
  g.add(...children);
  return g;
}

/** Materiais PBR de partida por cor (nunca desenhados: cada boneco clona os seus na superfície). */
const bases = new Map<string, MeshStandardMaterial>();
const base = (hex: string) => {
  let m = bases.get(hex);
  if (!m) bases.set(hex, (m = characterBaseMaterial({ color: hex })));
  return m;
};

/**
 * Boneco low-poly procedural (ADR 0007): ~150 triângulos, camisa na cor do jogador e chapéu de
 * palha. Altura total ~1,8 m com os pés em y = 0. Na superfície dos atores (F119): o PBR com a cor de
 * cada peça, as linhas e os nós na cor do jogador, um material por cor e por boneco (uniforms dele).
 */
export function createAvatarModel(atmosphere: AtmosphereUniforms, shirtHex: string): AvatarRig {
  const shirt = base(shirtHex);
  const skin = base(SKIN);
  const pants = base(PANTS);
  const straw = base(STRAW);
  const band = base(STRAW_BAND);

  const leftLeg = joint(-0.11, 0.8, 0, part(geometries.leg, pants, 0, -0.39, 0));
  const rightLeg = joint(0.11, 0.8, 0, part(geometries.leg, pants, 0, -0.39, 0));
  const leftArm = joint(-0.31, 1.36, 0, part(geometries.limb, shirt, 0, -0.27, 0));
  const rightArm = joint(0.31, 1.36, 0, part(geometries.limb, shirt, 0, -0.27, 0));
  const hand = new Group();
  hand.position.set(0, -0.58, -0.06);
  hand.rotation.set(-Math.PI / 2, 0, 0);
  rightArm.add(hand);
  const head = joint(
    0,
    1.42,
    0,
    part(geometries.head, skin, 0, 0.15, 0),
    part(geometries.brim, straw, 0, 0.31, 0),
    part(geometries.crown, straw, 0, 0.42, 0),
    part(geometries.band, band, 0, 0.35, 0),
  );
  const body = new Group();
  body.add(part(geometries.torso, shirt, 0, 1.1, 0), head, leftArm, rightArm, leftLeg, rightLeg);
  const root = new Group();
  root.add(body);
  // As primitivas sem índice (o baricêntrico da superfície é por canto).
  unindexTree(root);
  // Personagem: na máscara do contorno (F51), sem o rim noturno; o rim de luz é o dos avatares.
  const surface = applyCharacterSurface(root, actorStyle("heroi", shirtHex), { atmosphere, look: { rim: AVATAR_RIM, nightRim: false } });
  return { root, body, head, leftArm, rightArm, leftLeg, rightLeg, hand, surface };
}
